import {
  ChevronLeftIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import type { Option } from '../../../DesignSystem/Inputs/DropdownMenu';
import { DropdownMenu } from '../../../DesignSystem/Inputs/DropdownMenu';
import { IconButton } from '../../../DesignSystem/Inputs/IconButton';
import { TextField } from '../../../DesignSystem/Inputs/TextField';
import { FixedVirtualisedList } from '../../Shared/VirtualisedList';
import { CriteriaItem, CriteriaItemLoader } from './CriteriaItem';
import type { PreviewMembersProps } from './ShareModal';

const messages = defineMessages({
  previewModalSubtitle: {
    defaultMessage:
      'See who has permission to collaborate and view this collection.',
    id: 'KvBw23',
  },
  previewModalTitle: {
    defaultMessage: 'Preview list',
    id: 'y9y6sj',
  },
  noResultsFound: {
    defaultMessage: 'No results. Please try another search.',
    id: 'AX8eCS',
  },
  noResultsFoundWithFilter: {
    defaultMessage:
      'No results. Please try another search or search in “All roles”.',
    id: 'hrZaG4',
  },
});

export function PreviewMembers({
  options,
  onPreviewModalBackButtonClick,
  rules,
  hasNextPage,
  onLoadMore,
  onSearchOrFilterList,
  isLoadingPreview,
}: Omit<PreviewMembersProps, 'view'>) {
  const { formatMessage } = useIntl();

  const [selectedOption, setSelectedOption] = useState(
    options[0].value as string
  );

  const [searchTerm, setSearchTerm] = useState('');

  const itemCount =
    hasNextPage && rules.length ? rules.length + 1 : rules.length;

  const onOptionSelect = ({ value }: Option) => {
    setSelectedOption(value);
    onSearchOrFilterList({ filter: value, searchTerm });
  };

  const noResultsFoundLabel = useMemo(() => {
    if (selectedOption === 'all') {
      return formatMessage(messages.noResultsFound);
    }
    return formatMessage(messages.noResultsFoundWithFilter);
  }, [formatMessage, selectedOption]);

  return (
    <div className="w-full">
      <div className="flex flex-row items-start gap-1">
        <IconButton
          size="xSmall"
          variation="tertiaryLite"
          onClick={onPreviewModalBackButtonClick}
        >
          <ChevronLeftIcon className="h-4 w-4 text-gray-8" />
        </IconButton>
        <div className="flex flex-col gap-1">
          <TextStyle variant="lg-medium">
            {formatMessage(messages.previewModalTitle)}
          </TextStyle>
          <TextStyle variant="sm-regular">
            {formatMessage(messages.previewModalSubtitle)}
          </TextStyle>
        </div>
      </div>
      <div className="flex w-full gap-2 px-6 py-4">
        <div className="w-1/2">
          <TextField
            invalidText=""
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm((e.target as HTMLInputElement).value);
              onSearchOrFilterList({
                searchTerm: (e.target as HTMLInputElement).value,
                filter: selectedOption,
              });
            }}
            isInvalid={false}
            placeholder="Search members"
            connectedLeft={
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-8" />
            }
            inputClassName="focus:ring-0 focus:border-gray-5 text-sm"
            inputSize="md"
            aria-label="Search members on preview criteria list"
          />
        </div>
        <div className="w-1/2">
          <DropdownMenu
            value={selectedOption}
            options={options}
            onSelected={onOptionSelect}
            variant="bordered"
            className="m-0 w-full p-0"
            triggerClassName="w-full rounded-md focus:ring-0 focus:border-gray-5"
            contentClassName="w-[var(--radix-menubar-trigger-width)]"
            disabled={isLoadingPreview}
            isPortalDisabled
          />
        </div>
      </div>
      <section
        className="mt-2 min-h-[60px] px-6"
        style={{
          height: '400px',
        }}
      >
        {isLoadingPreview && (
          <>
            <CriteriaItemLoader showDropdownLoader={false} />
            <CriteriaItemLoader showDropdownLoader={false} />
          </>
        )}
        {!isLoadingPreview &&
          (rules.length ? (
            <FixedVirtualisedList
              itemCount={itemCount}
              items={rules}
              fixedItemSize={60}
              onLoadMore={onLoadMore}
              renderItem={(item) => {
                return <CriteriaItem {...item} />;
              }}
            />
          ) : (
            <TextStyle className="w-full pt-4 text-center text-gray-9">
              {noResultsFoundLabel}
            </TextStyle>
          ))}
      </section>
    </div>
  );
}
